/* eslint-disable sort-keys-fix/sort-keys-fix */
import Logo from './Logo';

// BASICS
export const baseColors = {
  beige: '#F2F0E3',
  black: '#000',
  bodyCopy: '#414042',
  darkGreen: '#005A4A',
  den: '#005A4A',
  gray: [
    '#fff', // 0 index
    '#f8f8f8',
    '#dedede',
    '#979797',
    '#3d3d3d',
    '#414042',
  ],
  greenTheme: '#00936F',
  highlighted: '#E9B84F',
  lightBlue: '#5BC4BF',
  red: '#f00101',
  siergras: '#5BC4BF',
  white: '#fff',
  yellow: '#E9B84F',
};

export const colors = {
  ...baseColors,
  background: baseColors.white,
  danger: baseColors.red,
  dark: baseColors.gray[5],
  darkGray: baseColors.gray[5],
  info: baseColors.greenTheme,
  light: baseColors.gray[2],
  lightGray: baseColors.gray[2],
  muted: baseColors.gray[1],
  primary: baseColors.greenTheme,
  primaryWhite: baseColors.white,
  primaryYellow: baseColors.yellow,
  secondary: baseColors.den,
  success: baseColors.greenTheme,
  tertiary: baseColors.beige,
  text: baseColors.gray[5],
  textMuted: baseColors.gray[3],
  warning: baseColors.yellow,
};

export const fonts = {
  body: 'museo-sans, sans-serif',
  heading: 'museo-sans, sans-serif',
  monospace: 'Menlo, monospace',
};

export const fontWeights = {
  body: 400,
  bold: 700,
  display: 300,
  heading: 400,
  headingAlt: 600,
  light: 300,
  normal: 400,
};

export const fontSizes = {
  0: '0.75rem',
  1: '0.875rem',
  2: '1rem',
  3: '1.313rem',
  4: '1.5rem',
  5: '1.75rem',
  6: '1.875rem',
  7: '2.5rem',
  8: '2.813rem',
  9: '4.5rem',
  10: '5.5rem',
  11: '6rem',
};

export const lineHeights = {
  body: 1.5,
  heading: 1.5,
  paragraph: 1.75,
};

export const sizes = {
  lg: '1150px',
  md: '720px',
  sm: '540px',
  xl: '1440px',
};

export const radii = {
  default: '0.25rem',
  pill: '50rem',
  lg: '0.3rem',
  sm: '0.2rem',
};

export const shadows = {
  default: '0 .5rem 1rem rgba(0, 0, 0, .15)',
  lg: '0 1rem 3rem rgba(0, 0, 0, .175)',
  sm: '0 .125rem .25rem rgba(0, 0, 0, .075)',
};

const heading = {
  fontFamily: fonts.heading,
  fontWeight: fontWeights.heading,
};

const display = {
  fontWeight: fontWeights.display,
  lineHeight: lineHeights.heading,
};

// variants
const typeStyles = {
  heading,
  display,
};

export const text = {
  caps: {
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
  },
  heading: {
    fontFamily: fonts.heading,
    fontWeight: fontWeights.heading,
    textTransform: 'uppercase',
  },
};

export const styles = {
  root: {
    fontFamily: fonts.body,
    fontWeight: fontWeights.display,
    backgroundColor: colors.white,
  },
  a: {
    '&:hover': {
      color: colors.primary,
    },
    color: colors.darkGray,
    textDecoration: 'underline',
  },
  blockquote: {
    backgroundColor: colors.greenTheme,
    color: colors.background,
    textTransform: 'normal',
    fontWeight: '500',
    fontStyle: 'normal',
  },
  h1: {
    ...heading,
    fontSize: { desktop: fontSizes[8], tablet: '2.188rem', mobile: fontSizes[6] },
    fontWeight: 900,
    lineHeight: { desktop: '54px', tablet: '42px', mobile: '36px' },
  },
  h2: {
    ...heading,
    fontSize: { desktop: fontSizes[7], tablet: fontSizes[6], mobile: '1.563rem' },
    fontWeight: 900,
    lineHeight: { desktop: '48px', tablet: '36px', mobile: '31px' },
  },
  h3: {
    ...heading,
    fontSize: { desktop: fontSizes[6], tablet: fontSizes[3], mobile: fontSizes[3] },
    fontWeight: 700,
    lineHeight: { desktop: '36px', tablet: '25px', mobile: '25px' },
  },
  h4: {
    ...heading,
    fontSize: { desktop: fontSizes[4], tablet: '1.25rem', mobile: '1.125rem' },
    fontWeight: 700,
    lineHeight: { desktop: '29px', tablet: '24px', mobile: '22px' },
  },
  h5: {
    ...heading,
    fontSize: { desktop: fontSizes[3], tablet: '1.188rem', mobile: fontSizes[2] },
    fontWeight: 700,
    lineHeight: { desktop: '25px', tablet: '24px', mobile: '19px' },
  },
  highlight: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  img: {
    maxWidth: '100%',
    height: 'auto',
  },
  inlineCode: {
    color: colors.darkGray,
  },
  li: {
    bulletColor: colors.primary,
    bulletBorderRadius: '10px',
  },
  p: {
    margin: '0',
    marginBottom: '1.7rem',
    lineHeight: lineHeights.paragraph,
    fontSize: fontSizes[2],
  },
  rteHighlight: {
    hoverColor: colors.highlighted,
    color: colors.primary,
    fontWeight: '700',
    textDecoration: 'underline',
    isFooter: colors.white,
  },
  table: {
    borderCollapse: 'collapse',
    color: colors.gray[3],
    marginBottom: '3rem',
    width: '100%',
  },
  th: {
    verticalAlign: 'bottom',
    borderTopWidth: '2px',
    borderTopStyle: 'solid',
    borderTopColor: colors.gray[3],
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: colors.gray[3],
    padding: '.75rem',
    textAlign: 'inherit',
  },
  td: {
    borderBottomWidth: 2,
    borderBottomStyle: 'solid',
    borderBottomColor: colors.gray[3],
    verticalAlign: 'top',
    padding: '.75rem',
  },
};

const buttonDefaults = {
  borderRadius: '0px',
  cursor: 'pointer',
  fontFamily: fonts.body,
  fontSize: fontSizes[1],
  fontWeight: 700,
  height: 'auto',
  padding: '13px 35px 12px',
};

export const buttons = {
  disabled: {
    ...buttonDefaults,
    backgroundColor: colors.muted,
    color: colors.textMuted,
    cursor: 'default',
  },
  primary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.darkGreen,
      borderColor: colors.darkGreen,
    },
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    borderStyle: 'solid',
    borderWidth: '2px',
    color: colors.white,
  },
  'primary-with-background': {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.darkGreen,
      borderColor: colors.darkGreen,
      color: colors.white,
    },
    backgroundColor: colors.white,
    borderColor: colors.white,
    borderStyle: 'solid',
    borderWidth: '2px',
    color: colors.primary,
  },
  secondary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.white,
      borderColor: colors.darkGreen,
      color: colors.darkGreen,
    },
    backgroundColor: colors.white,
    borderColor: colors.primary,
    borderStyle: 'solid',
    borderWidth: '2px',
    color: colors.primary,
  },
  tertiary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.white,
      borderColor: colors.white,
      color: colors.darkGreen,
    },
    backgroundColor: 'transparent',
    borderColor: colors.white,
    borderStyle: 'solid',
    borderWidth: '2px',
    color: colors.white,
  },
};

// COMPONENTS
export const adviceCardModule = {
  backgroundColor: colors.primary,
};

export const articleDetail = {
  introduction: colors.text,
  tags: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  title: colors.text,
};

export const articleListing = {
  background: colors.primary,
  filter: colors.text,
  hover: colors.secondary,
  mobile: {
    title: colors.primary,
    text: colors.text,
    background: colors.beige,
  },
  text: colors.white,
  title: colors.text,
};

export const breadcrumb = {
  backgroundColor: colors.beige,
};

export const cardCtas = {
  arrowColor: colors.greenTheme,
  arrowHoverColor: colors.darkGreen,
  color: colors.darkGreen,
  fontSize: '0.75rem',
  hoverColor: colors.darkGreen,
  letterSpacing: '2px',
  lineHeight: '13px',
  textTransform: 'uppercase',
};

export const cards = {
  article: {
    description: colors.text,
    link: {
      '&:hover': {
        color: colors.secondary,
        textDecoration: 'underline',
        opacity: 1,
      },
      color: colors.primary,
    },
    title: colors.text,
  },
  logo: {
    color: colors.text,
  },
};

export const carousel = {
  controls: {
    regular: {
      backgroundColor: colors.primary,
      color: colors.white,
      hover: {
        backgroundColor: colors.secondary,
        color: colors.white,
      },
    },
    hero: {
      backgroundColor: colors.white,
      color: colors.primary,
      stroke: colors.white,
      opacity: '0.8',
      maskArrowOffsets: {
        top: ['calc(50% - 6px)', null, null, 'calc(50% - 57px)', 'calc(50% - 57px)'],
        horizontal: ['', '0', '40px', '80px'],
      },
      hover: {
        backgroundColor: colors.white,
        color: colors.primary,
        stroke: colors.white,
      },
    },
  },
  indicator: colors.secondary,
  link: {
    '&:hover': {
      color: colors.secondary,
      opacity: 1,
      textDecoration: 'underline',
    },
    color: colors.primary,
  },
  text: colors.text,
};

export const categoryCards = {
  backgroundColor: colors.greenTheme,
};

export const categorySection = {
  background: colors.primary,
  boxShadow: null,
  chevron: {
    fill: colors.white,
    stroke: colors.primary,
  },
  color: colors.primaryWhite,
  date: colors.primary,
  dateColor: colors.primaryWhite,
  headingColor: colors.dark,
};

export const columns = {
  imageCard: colors.text,
  imageCardTitle: colors.text,
  infoBlock: {
    background: colors.beige,
    button: {
      ...buttonDefaults,
      '&:hover': {
        backgroundColor: colors.secondary,
        textDecoration: 'none',
      },
      backgroundColor: colors.primary,
      color: colors.white,
    },
    description: colors.text,
    title: colors.text,
  },
};

export const contactForm = {
  heading: {
    color: colors.primary,
  },
};

export const contacts = {
  office: {
    mapTextColor: colors.primary,
    mapTextFontSize: '0.875rem',
    mapTextFontWeight: 'normal',
    mapTextLetterSpacing: '2.33px',
    mapTextLineHeight: '1.71',
    mapTextTransform: 'uppercase',
    openingHoursFontSize: '1rem',
  },
  description: colors.text,
  title: colors.text,
};

export const cookieBanner = {
  backgroundColor: colors.primary,
};

export const ctaBanner = {
  focusImage: {
    backgroundColor: colors.primary,
    color: colors.white,
    titleColor: colors.white,
  },
  focusText: {
    backgroundColor: colors.yellow,
    color: colors.bodyCopy,
  },
};

export const ctaGroup = {
  title: colors.text,
  chevron: {
    opacity: 1,
  },
};

export const customBlockCards = {
  backgroundColor: colors.darkGreen,
  color: colors.darkGreen,
};

export const dealer = {
  textColor: colors.primary,
};

export const documentDownload = {
  arrowColor: colors.greenTheme,
  arrowHoverColor: colors.darkGreen,
  color: colors.greenTheme,
  hoverColor: colors.darkGreen,
};

export const footer = {
  activeColor: colors.highlighted,
  backgroundColor: colors.white,
  color: colors.darkGray,
  minHeight: '160px',
};

export const formInputs = {
  activeColor: colors.primary,
  borderRadius: '0px',
  label: {
    fontSizes: ['12px', null, '16px'],
    lineHeights: ['21px', null, '26px'],
  },
  radioInputBorderRadius: '50%',
  radioSpanBorderRadius: '50%',
};

export const header = {
  backgroundColor: colors.white,
  color: colors.darkGray,
  activeColor: colors.secondary,
};

export const headerBottom = {
  activeColor: colors.white,
  activeFontWeight: 'bold',
  backgroundColor: colors.primaryYellow,
  color: colors.white,
  fontSize: '12px',
  fontWeight: 'normal',
};

export const heroBannerButton = {
  'No Background': {
    '&:hover': {
      backgroundColor: colors.white,
      borderColor: colors.white,
      color: colors.secondary,
    },
    backgroundColor: 'transparent',
    borderColor: colors.white,
    borderStyle: 'solid',
    borderWidth: '2px',
    color: colors.white,
    height: '42px',
    justifyContent: 'center',
  },
  'White Background': {
    '&:hover': {
      backgroundColor: colors.darkGreen,
      border: 'none',
      color: colors.white,
    },
    backgroundColor: colors.primary,
    color: colors.white,
    justifyContent: 'center',
    minHeight: '42px',
    padding: '13px 35px 12px',
  },
  arrow: {
    display: 'none',
  },
};

export const heroSection = {
  titleFontWeight: 'normal',
};

export const imageAndText = {
  color: colors.primary,
};

export const infoBlock = {
  background: colors.beige,
  button: {
    '&:hover': {
      backgroundColor: colors.secondary,
      textDecoration: 'none',
    },
    backgroundColor: colors.primary,
    color: colors.white,
  },
  description: colors.text,
  title: colors.text,
};

export const infoSection = {
  backgroundColor: colors.darkGreen,
  color: colors.white,
  title: colors.white,
};

export const ingredients = {
  backgroundColor: colors.primary,
  color: colors.white,
  hoverColor: colors.secondary,
};

export const media = {
  backgroundColor: colors.darkGreen,
  titleFontWeight: 'bold',
  titleTextTransform: 'normal',
};

export const mobileNavigation = {
  arrowColor: colors.primary,
  chevronColor: colors.primary,
};

export const navItems = {
  fontSize: '0.75rem',
  letterSpacing: '2px',
};

export const newsListing = {
  titleFontWeight: 'normal',
};

export const ownerDetail = {
  introduction: colors.text,
  storeCard: {
    background: colors.tertiary,
  },
  tags: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  title: colors.text,
};

export const product = {
  backgroundColor: colors.secondary,
  color: colors.white,
};

export const productListing = {
  titleFontWeight: 'normal',
};

export const salesPerson = {
  borderColor: colors.primary,
};

export const search = {
  activeColor: colors.primary,
  borderRadius: '20px',
};

export const shortBanner = {
  backgroundColor: colors.primaryYellow,
  color: colors.white,
  titleFontWeight: 'bold',
  titleTextTransform: 'none',
};

export const table = {
  headerBackgroundColor: colors.darkGreen,
  headerColor: colors.white,
  scrollbarColor: colors.secondary,
};

export const upComingEvents = {
  backgroundColor: colors.primary,
  color: colors.white,
};

export default {
  adviceCardModule,
  articleDetail,
  articleListing,
  breadcrumb,
  buttons,
  cardCtas,
  cards,
  carousel,
  categoryCards,
  categorySection,
  colors,
  columns,
  contactForm,
  contacts,
  cookieBanner,
  ctaBanner,
  ctaGroup,
  customBlockCards,
  dealer,
  documentDownload,
  fontSizes,
  fonts,
  fontWeights,
  footer,
  formInputs,
  header,
  headerBottom,
  heroBannerButton,
  heroSection,
  imageAndText,
  infoBlock,
  infoSection,
  ingredients,
  lineHeights,
  logo: Logo,
  media,
  mobileNavigation,
  name: 'AveveAgrarisch',
  navItems,
  newsListing,
  ownerDetail,
  product,
  productListing,
  radii,
  salesPerson,
  search,
  shadows,
  shortBanner,
  sizes,
  styles,
  table,
  text,
  typeStyles,
  upComingEvents,
};
